import { Button, Form, Input, Steps, Typography, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSelfAddFarm } from 'hooks/useFarms';
import { organisationsQueryKey, useUserOrganisations } from 'hooks/useOrganisations';
import { accountInfoQueryKey } from 'api/account';
import { storageKeys } from 'static';
import { setAnalyticsFarmContext } from 'analytics/ga4';
import { notifyZapierFarmRegistered } from 'utils/zapierFarmRegistered';
import { getManagedOrganisationId } from 'utils/companyHub';
import { TFunction } from 'types';
import { OrgFormPanel } from './OrgFormPanel';
import { OrgPageHero } from './OrgPageHero';
import { OrgPrimaryButton } from './OrgPrimaryButton';
import { OrganisationsPageLayout } from './OrganisationsPageLayout';
import { orgFormMaxWidth } from './organisationsUi';
import { useState } from 'react';

export default function OrganisationSetupWizard() {
  const { t }: TFunction = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [farmForm] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [addressForm] = Form.useForm();
  // Persist step values in state — Ant Design clears unmounted step Form fields.
  const [farmName, setFarmName] = useState('');
  const [contactPerson, setContactPerson] = useState({ firstName: '', lastName: '' });
  const { mutateAsync: selfAddFarm } = useSelfAddFarm();
  const { data: organisations } = useUserOrganisations();
  const organisationId = getManagedOrganisationId(organisations);

  const stepTitles = [t('WhatIsYourFarmName'), t('OrgFlowContactPerson'), t('WhatIsYourAddress')];

  const finish = async () => {
    const addressValues = await addressForm.validateFields();
    const resolvedFarmName = farmName.trim();
    if (!resolvedFarmName) {
      message.error(t('AnErrorOccurred'));
      setCurrent(0);
      return;
    }

    setSubmitting(true);
    try {
      const data = await selfAddFarm({
        farmName: resolvedFarmName,
        organisationId: organisationId || undefined,
        countryId: 45,
        prodPlaceData: [],
        farmCode: '',
        cdbSynchEnabled: false,
        addressData: {
          address1: String(addressValues.address1 || '').trim(),
          city: String(addressValues.city || '').trim(),
          district: '',
          zipCode: '',
          address2: '',
        },
        contactData: {
          firstName: contactPerson.firstName.trim(),
          lastName: contactPerson.lastName.trim(),
          companyName: '',
          companyRegistration: '',
          companyType: 'O',
          email: '',
          fax: '',
          mobilePhone: '',
          phone: '',
        },
      });
      const farmId = data?.id ?? data?.farmId;
      if (farmId) {
        sessionStorage.setItem(storageKeys.RecentFarmId, String(farmId));
        sessionStorage.setItem(storageKeys.RecentFarmName, resolvedFarmName);
      }
      setAnalyticsFarmContext(farmId ? String(farmId) : undefined, resolvedFarmName);
      void notifyZapierFarmRegistered({
        farmId,
        farmName: resolvedFarmName,
        firstName: contactPerson.firstName.trim(),
        lastName: contactPerson.lastName.trim(),
        address: String(addressValues.address1 || '').trim(),
        city: String(addressValues.city || '').trim(),
      });
      void queryClient.invalidateQueries(accountInfoQueryKey());
      void queryClient.invalidateQueries(organisationsQueryKey());
      message.success(t('OrgFlowSetupComplete'));
      navigate('/app-selection', { replace: true });
    } catch {
      message.error(t('AnErrorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const nextFromFarm = async () => {
    const values = await farmForm.validateFields();
    setFarmName(String(values.farmName || '').trim());
    setCurrent(1);
  };

  const nextFromContact = async () => {
    const values = await contactForm.validateFields();
    setContactPerson({
      firstName: String(values.firstName || '').trim(),
      lastName: String(values.lastName || '').trim(),
    });
    setCurrent(2);
  };

  return (
    <OrganisationsPageLayout>
      <OrgPageHero
        breadcrumbItems={[
          { title: <Link to="/companies">{t('CompaniesAndFarmsPageTitle')}</Link> },
          { title: t('OrgFlowSetupTitle') },
        ]}
        title={t('OrgFlowSetupTitle')}
        subtitle={t('OrgFlowSetupSubtitle')}
      />

      <div style={{ maxWidth: orgFormMaxWidth, margin: '0 auto' }}>
        <Steps
          current={current}
          size="small"
          style={{ marginBottom: 24 }}
          items={stepTitles.map((title) => ({ title }))}
        />

        <OrgFormPanel>
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {stepTitles[current]}
          </Typography.Title>

          {current === 0 && (
            <Form form={farmForm} layout="vertical" initialValues={{ farmName }}>
              <Form.Item name="farmName" label={t('FarmName')} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
              <OrgPrimaryButton block loading={submitting} onClick={nextFromFarm}>
                {t('Continue')}
              </OrgPrimaryButton>
            </Form>
          )}

          {current === 1 && (
            <Form
              form={contactForm}
              layout="vertical"
              initialValues={{
                firstName: contactPerson.firstName,
                lastName: contactPerson.lastName,
              }}
            >
              <Form.Item name="firstName" label={t('FirstName')} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
              <Form.Item name="lastName" label={t('LastName')} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
              <OrgPrimaryButton block loading={submitting} onClick={nextFromContact}>
                {t('Continue')}
              </OrgPrimaryButton>
              <Button type="link" block onClick={() => setCurrent(0)}>
                {t('Back')}
              </Button>
            </Form>
          )}

          {current === 2 && (
            <Form form={addressForm} layout="vertical">
              <Form.Item name="address1" label={t('Address')} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
              <Form.Item name="city" label={t('City')} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
              <OrgPrimaryButton block loading={submitting} onClick={finish}>
                {t('OrgFlowFinishSetup')}
              </OrgPrimaryButton>
              <Button type="link" block onClick={() => setCurrent(1)}>
                {t('Back')}
              </Button>
            </Form>
          )}
        </OrgFormPanel>
      </div>
    </OrganisationsPageLayout>
  );
}
